import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { FormField } from '../../entities/form.entity';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'app-field-file-upload',
  templateUrl: './field-file-upload.component.html',
  styleUrls: ['./field-file-upload.component.scss'],
})
export class FieldFileUploadComponent implements OnChanges {
  @Input() field!: FormField;
  @Input() disabled: boolean = false;
  @Output() onFieldChange = new EventEmitter<any>();

  extensions: string[] = [];
  fileName: string = '';
  fileBuffer: ArrayBuffer | null = null;
  fileUploadLimit: number = 10;
  files: File[] = [];
  fileNames: string[] = [];
  multipleFileUploadLimit: number | null = null;


  private filesData: any[] = [];

  constructor(private hotToastService: HotToastService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['field'] && changes['field'].currentValue) {
      // Set up extensions
      if (this.field.metadata.acceptedExtensions) {
        this.extensions = this.field.metadata.acceptedExtensions.map(
          (ext) => '.' + ext.toLocaleLowerCase().replace(/^\./, '')
        );
      }


      this.multipleFileUploadLimit = this.field.metadata.useMultipleFileUpload
        ? this.field.metadata.multipleFileUploadLimit || null
        : 1;

      if (this.field.value) {
        const valueArray = Array.isArray(this.field.value)
          ? this.field.value
          : [this.field.value];
        this.filesData = [...valueArray];
        this.files = valueArray.map((f: any) => f.file).filter(Boolean);
        this.fileNames = valueArray.map((f: any) => f.fileName).filter(Boolean);
      }
    }
  }

  fileChange(event: Event): void {
    const element = event.target as HTMLInputElement;
    const files = element.files;
    const maxFileSize = (this.fileUploadLimit || 10) * 1024 * 1024;

    if (files) {
      const totalFiles = this.files.length + files.length;
      const fileLimit = this.multipleFileUploadLimit || Infinity;

      if (totalFiles > fileLimit) {
        this.hotToastService.error(
          `You can only upload up to ${fileLimit} file${fileLimit === 1 ? '' : 's'}`
        );
        return;
      }


      Array.from(files).forEach((file) => {
        if (file.size > maxFileSize) {
          this.hotToastService.error(
            `File ${file.name} exceeds the size limit of ${this.fileUploadLimit} MB`
          );
          return;
        }
        this.validateAndReadFile(file);
      });

      element.value = '';
    }
  }

  validateAndReadFile(file: File): void {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.extensions.includes(fileExtension)) {
      this.hotToastService.error(
        'Invalid file type. Accepted types: ' + this.extensions.join(', ')
      );
      return;
    }

    if (this.fileNames.includes(file.name)) {
      this.hotToastService.error(`File "${file.name}" has already been added`);
      return;
    }

    this.files.push(file);
    this.fileNames.push(file.name);
    this.fileName = file.name;
    this.emitFileChange(file);
  }

  emitFileChange(file: File): void {
    if (!file) {
      this.emitValue([]);
      return;
    }

    const newFileData = {
      key: uuid(),
      keyFile: `${uuid()}${this.getFileExtension(file.name)}`,
      mimeType: this.getMimeType(file.name),
      fileName: file.name,
      file,
      isValid: true,
    };


    this.filesData.push(newFileData);

    this.emitValue(this.filesData);
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.fileNames.splice(index, 1);
    this.filesData.splice(index, 1);


    this.emitValue(this.filesData);
  }

  private emitValue(filesData: any[]) {
    this.onFieldChange.emit({
      isFile: true,
      fieldId: this.field.fieldId,
      isValid: filesData.length > 0,
      value: filesData,
    });
  }

  private getFileExtension(filename: string): string {
    return '.' + filename.split('.').pop()?.toLowerCase();
  }

  convertToBase64(buffer: any) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  getMimeType(filename: string): string {
    const mimeTypes: { [key: string]: string; } = {
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.pdf': 'application/pdf',
      '.doc': 'application/msword',
      '.docx':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls': 'application/vnd.ms-excel',
      '.xlsx':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.ppt': 'application/vnd.ms-powerpoint',
      '.pptx':
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.txt': 'text/plain',
    };
    const extension = filename
      .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
      .toLowerCase();
    return mimeTypes['.' + extension] || 'application/octet-stream';
  }

  get canAddMoreFiles(): boolean {
    if (!this.field.metadata.useMultipleFileUpload) {
      return this.files.length === 0;
    }

    if (this.multipleFileUploadLimit === null) {
      return true;
    }

    return this.files.length < this.multipleFileUploadLimit;
  }
}
